/** Intent detection — keyword/pattern matching with priority phrases. */

import { isFaqQuery } from "assistant/knowledge";
import {
  extractSearchTerm,
  extractTeacherStoryTerm,
  isTeacherFollowUpQuery,
  isTeacherLookupQuery,
  isTeacherSearchWithoutName,
  isTeacherNameFollowUp,
  resolveTeacherSearchReply,
} from "assistant/teacherQuery";

export {
  extractSearchTerm,
  extractTeacherStoryTerm,
  isTeacherFollowUpQuery,
  isTeacherLookupQuery,
  isTeacherSearchWithoutName,
  isTeacherNameFollowUp,
  resolveTeacherSearchReply,
} from "assistant/teacherQuery";

function normalize(text) {
  return String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

/** High-priority phrase checks (run before generic patterns). */
const PRIORITY_RULES = [
  {
    id: "teacher_story",
    test: (q) =>
      isTeacherFollowUpQuery(q) ||
      (/\b(know about|tell me about|learn about|info on|information on|who is|who's|qui est)\b/.test(q) &&
        /\b(teacher|enseignant)\b/.test(q)) ||
      /\b(teacher story|full story|cross.?module|status of teacher|teacher profile|fiche enseignant|histoire enseignant|profil enseignant|teacher overview|show profile|open profile)\b/.test(
        q
      ) ||
      /\b(registration\s*(?:no|number)|reg\s*(?:no|number)|matricule|numero\s*d['']?enregistrement)\b/.test(q) ||
      /\b(is|are)\s+.+\s+(assigned|on leave|attending|present)\b/.test(q) ||
      /\b(est.+(affect|assign|conge|present))\b/.test(q) ||
      /\b(est ce que|est-ce que).+(affect|assign|conge)\b/.test(q) ||
      /\b(affect.*conge|conge.*present|assigned.*leave|leave.*attend)\b/.test(q),
  },
  {
    id: "search_teacher",
    test: (q) =>
      isTeacherLookupQuery(q) ||
      /\b(chercher enseignant|trouver enseignant|rechercher enseignant|search teacher|find teacher|look up teacher|teacher named|enseignant nomme|enseignant nommé|lookup teacher|find enseignant|find a teacher|locate a teacher|help me find a teacher|help find teacher)\b/.test(
        q
      ),
  },
  {
    id: "faq",
    test: (q) =>
      /\b(how to change password|change password|changer mot de passe|reset password|forgot password|mot de passe)\b/.test(
        q
      ) ||
      /\b(how to create (?:a )?(?:portal )?user|create (?:a )?(?:portal )?user|creer un utilisateur|créer un utilisateur)\b/.test(
        q
      ) ||
      /\b(how to change (?:the )?language|changer la langue|change language)\b/.test(q),
  },
  {
    id: "help_page",
    test: (q) =>
      !isTeacherLookupQuery(q) &&
      /\b(aide|help|assist)\b/.test(q) &&
      (/\b(cette page|this page|sur cette page|on this page|ici|here)\b/.test(q) ||
        /\b(page actuelle|current page)\b/.test(q) ||
        (/\b(aide|help)\b/.test(q) && /\bpage\b/.test(q))),
  },
  {
    id: "capabilities",
    test: (q) =>
      !isTeacherLookupQuery(q) &&
      (/\b(que peux tu|que pouvez vous|quoi faire|what can you|what do you|what you can|capabilities|fonctionnalites|fonctionnalités|features|abilities|tu fais quoi|you do for me|help me with|peux tu faire|pouvez vous faire)\b/.test(
        q
      ) ||
        q.includes("what can you do") ||
        q.includes("what you can do") ||
        q.includes("que peux-tu faire") ||
        q.includes("que peux tu faire")),
  },
  {
    id: "help_page",
    test: (q) =>
      q === "aide" ||
      q === "help" ||
      q.startsWith("comment utiliser") ||
      q.startsWith("how to use") ||
      q.includes("comment ca marche") ||
      q.includes("how does this work"),
  },
  {
    id: "dashboard_summary",
    test: (q) =>
      /\b(resume du portail|resume portail|portal summary|apercu portail|aperçu portail|situation globale)\b/.test(q),
  },
  {
    id: "count_leaves",
    test: (q) =>
      /\b(conges en attente|congés en attente|pending leave|leave pending|demandes en attente)\b/.test(q),
  },
  {
    id: "wizard_affectation",
    test: (q) => /\b(guide affectation|assignment guide|wizard affectation|pas a pas affectation)\b/.test(q),
  },
  {
    id: "wizard_leave",
    test: (q) => /\b(guide conge|guide congé|leave guide|wizard leave)\b/.test(q),
  },
  {
    id: "wizard_teacher",
    test: (q) => /\b(guide enseignant|teacher guide|wizard teacher)\b/.test(q),
  },
  {
    id: "wizard_attendance",
    test: (q) => /\b(guide presence|guide présence|attendance guide|wizard attendance)\b/.test(q),
  },
  {
    id: "license_summary",
    test: (q) =>
      /\b(license quota|licence quota|quota licence|teacher license|teacher licence|licences enseignant|licences enseignants|remaining license|remaining licence|creneaux licence|créneaux licence|how many license|how many licence|combien licence|combien de licence)\b/.test(
        q
      ) ||
      /\b(unlicensed|non licencie|non licencié|non licencies|non licenciés)\b/.test(q),
  },
  {
    id: "area_attendance",
    test: (q) =>
      /\b(area attendance|zone attendance|synthese presence|synthèse présence|synthese presence zone|presence zone|présence zone|attendance by zone|attendance summary zone)\b/.test(
        q
      ),
  },
  {
    id: "establishment_attendance",
    test: (q) =>
      /\b(establishment attendance|attendance report|rapport presence|rapport présence|presence etablissement|présence établissement|presence establishment)\b/.test(
        q
      ),
  },
  {
    id: "insights",
    test: (q) => /\b(alertes|alerts|a surveiller|à surveiller|need attention|what needs)\b/.test(q),
  },
];

const INTENTS = [
  {
    id: "greeting",
    patterns: {
      fr: ["bonjour", "salut", "bonsoir", "hello", "coucou", "hey"],
      en: ["hello", "hi", "hey", "good morning", "good afternoon"],
    },
  },
  {
    id: "thanks",
    patterns: {
      fr: ["merci", "thanks", "parfait", "super", "genial"],
      en: ["thanks", "thank you", "great", "perfect"],
    },
  },
  {
    id: "help_page",
    patterns: {
      fr: ["aide", "help", "comment", "how to", "utiliser", "guide", "expliquer", "explique", "assistance"],
      en: ["help", "how to", "guide", "explain", "use this", "what is", "assistance"],
    },
  },
  {
    id: "capabilities",
    patterns: {
      fr: ["que peux tu", "quoi faire", "fonctionnalites", "capacites", "tu fais quoi"],
      en: ["what can you", "what do you", "capabilities", "features", "help me with"],
    },
  },
  {
    id: "navigate",
    patterns: {
      fr: ["aller a", "aller à", "ouvrir", "naviguer", "acceder", "accéder", "montre moi", "mene moi", "mène moi", "go to", "open"],
      en: ["go to", "open", "navigate", "take me to", "show me", "bring me"],
    },
  },
  {
    id: "count_teachers",
    patterns: {
      fr: ["combien enseignant", "nombre enseignant", "total enseignant", "how many teacher"],
      en: ["how many teacher", "teacher count", "total teacher", "number of teacher"],
    },
  },
  {
    id: "count_recruiters",
    patterns: {
      fr: ["combien recruteur", "nombre recruteur", "total recruteur"],
      en: ["how many recruiter", "recruiter count", "total recruiter"],
    },
  },
  {
    id: "count_leaves",
    patterns: {
      fr: ["combien conge", "combien congé", "nombre conge", "total conge"],
      en: ["how many leave", "leave count", "total leave"],
    },
  },
  {
    id: "count_assignments",
    patterns: {
      fr: ["combien affectation", "total affectation", "nombre affectation"],
      en: ["how many assignment", "assignment count", "total assignment"],
    },
  },
  {
    id: "count_establishments",
    patterns: {
      fr: ["combien etablissement", "combien établissement", "total etablissement"],
      en: ["how many establishment", "establishment count", "total establishment"],
    },
  },
  {
    id: "dashboard_summary",
    patterns: {
      fr: ["resume", "résumé", "summary", "apercu", "aperçu", "overview", "kpi", "statistique globale"],
      en: ["summary", "overview", "situation", "kpi", "dashboard stats", "global stats"],
    },
  },
  {
    id: "search_teacher",
    patterns: {
      fr: ["liste enseignant", "annuaire enseignant"],
      en: ["teacher list", "teacher directory"],
    },
  },
  {
    id: "license_summary",
    patterns: {
      fr: ["licences", "licence enseignant", "quota licence", "créneaux licence"],
      en: ["licenses", "teacher licenses", "license quota", "licence slots"],
    },
  },
  {
    id: "wizard_affectation",
    patterns: {
      fr: ["etapes affectation", "étapes affectation", "creer affectation", "créer affectation"],
      en: ["steps assignment", "step by step assignment", "create assignment"],
    },
  },
  {
    id: "wizard_leave",
    patterns: {
      fr: ["etapes conge", "étapes congé", "soumettre conge", "soumettre congé"],
      en: ["steps leave", "step by step leave", "submit leave"],
    },
  },
  {
    id: "wizard_teacher",
    patterns: {
      fr: ["ajouter enseignant", "creer enseignant", "créer enseignant"],
      en: ["add teacher", "create teacher", "new teacher"],
    },
  },
  {
    id: "wizard_attendance",
    patterns: {
      fr: ["generer rapport", "générer rapport", "rapport presence", "rapport présence"],
      en: ["generate report", "attendance report"],
    },
  },
  {
    id: "insights",
    patterns: {
      fr: ["alerte", "proactif", "insights", "attention"],
      en: ["alert", "insights", "proactive", "watch"],
    },
  },
  {
    id: "clear",
    patterns: {
      fr: ["effacer", "reinitialiser", "réinitialiser", "clear chat", "nouvelle conversation"],
      en: ["clear", "reset", "new conversation", "clear chat"],
    },
  },
];

export function detectIntent(input, language, roleBucket = "other") {
  const q = normalize(input);
  if (!q) return { id: "empty", confidence: 0 };

  if (isFaqQuery(input, language, roleBucket)) {
    return { id: "faq", confidence: 10 };
  }

  for (const rule of PRIORITY_RULES) {
    if (rule.test(q)) {
      return { id: rule.id, confidence: 10 };
    }
  }

  let best = { id: "unknown", confidence: 0 };

  INTENTS.forEach((intent) => {
    const patterns = [...(intent.patterns[language] || []), ...(intent.patterns.fr || [])];
    patterns.forEach((pattern) => {
      const p = normalize(pattern);
      if (q === p) {
        if (3 > best.confidence) best = { id: intent.id, confidence: 3 };
      } else if (p.length >= 4 && q.includes(p)) {
        // Avoid "help" matching teacher lookup ("help me find a teacher")
        if (intent.id === "help_page" && isTeacherLookupQuery(q)) return;
        if (intent.id === "help_page" && isFaqQuery(input, language, roleBucket)) return;
        if (intent.id === "capabilities" && isTeacherLookupQuery(q)) return;
        const conf = 1.5 + p.length / 50;
        if (conf > best.confidence) best = { id: intent.id, confidence: conf };
      } else if (p.length >= 3 && q.startsWith(p)) {
        if (intent.id === "help_page" && isTeacherLookupQuery(q)) return;
        if (intent.id === "help_page" && isFaqQuery(input, language, roleBucket)) return;
        const conf = 1.2 + p.length / 50;
        if (conf > best.confidence) best = { id: intent.id, confidence: conf };
      }
    });
  });

  if (best.id === "help_page" && isTeacherLookupQuery(q)) {
    return { id: "search_teacher", confidence: 5 };
  }

  if (best.id === "help_page" && isFaqQuery(input, language, roleBucket)) {
    return { id: "faq", confidence: 10 };
  }

  return best;
}

export function isHelpOnPageQuery(input) {
  const q = normalize(input);
  return PRIORITY_RULES.some((r) => r.id === "help_page" && r.test(q));
}

export function isCapabilitiesQuery(input) {
  const q = normalize(input);
  return PRIORITY_RULES.some((r) => r.id === "capabilities" && r.test(q));
}

const PORTAL_TOPIC_RE =
  /\b(everifpro|e.?verif|portail|portal|enseignant|teacher|prof|conge|congé|leave|absence|affectation|assignment|etablissement|établissement|establishment|ecole|école|licence|license|presence|présence|attendance|recruteur|recruiter|dashboard|statistique|statistics|conge|congé|matricule|check.?in|pointer|mobile|quota|csv|parametre|paramètre|settings|utilisateur|user|admin|observateur|observer|notification|carte|map|synthese|synthèse|rapport|report|fiche|profile|profil|wizard|guide|password|mot de passe)\b/;

const OFF_TOPIC_RE =
  /\b(weather|meteo|météo|temperature|forecast|football|soccer|basketball|recipe|recette|cuisine|cook|bitcoin|crypto|stock market|bourse|movie|film|netflix|joke|blague|raconte|poem|poeme|poème|who is the president|president des|capital of|capitale de|play music|song|chanson|translate this|traduis|write code|python code|javascript code|homework|math problem|2\+2|calculate sin|cosine)\b/;

/** General web / chit-chat — not portal-related. */
export function isOffTopicQuery(input) {
  const q = normalize(input);
  if (!q || q.length < 4) return false;
  if (PORTAL_TOPIC_RE.test(q)) return false;
  if (OFF_TOPIC_RE.test(q)) return true;
  // Very short vague questions with no portal keywords
  if (q.length <= 28 && /^(who|what|why|how|qui|quoi|pourquoi|comment)\s/.test(q) && !PORTAL_TOPIC_RE.test(q)) {
    return true;
  }
  return false;
}
